import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {(() => void) | null} */
  #unregisterOverlay = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    customElements.whenDefined('header-component').then(() => {
      if (this.isConnected) this.#registerOverlay();
    });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    document.removeEventListener('click', this.#handleDocumentClick, true);
    this.#unregisterOverlay?.();
    this.#unregisterOverlay = null;
    this.#historyAbortController?.abort();
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  #registerOverlay() {
    if (this.#unregisterOverlay) return;

    this.#unregisterOverlay = this.headerComponent?.registerOverlay?.(this, {
      id: 'cart',
      getPanel: () => this.refs.dialog ?? null,
      getTrigger: () => this.querySelector('[data-testid="cart-drawer-trigger"]'),
      close: (options = {}) => {
        void this.closeDialog({
          skipAnimation: options.immediate === true,
          restoreFocus: options.restoreFocus === true,
        });
      },
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#registerOverlay();
    this.showDialog();
    this.headerComponent?.claimOverlay?.(this, {
      panel: this.refs.dialog,
      trigger: this.querySelector('[data-testid="cart-drawer-trigger"]'),
    });
    requestAnimationFrame(() => this.headerComponent?.syncOverlayGeometry?.(this));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Opens the cart as a non-modal header panel so header actions can switch overlays.
   */
  showDialog() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || dialog.open) return;

    dialog.show();
    this.#isClosing = false;
    this.dispatchEvent(new DialogOpenEvent());

    document.addEventListener('click', this.#handleDocumentClick, true);
  }

  /**
   * Closes the cart panel with the drawer animation unless an exclusive overlay switch needs an immediate close.
   * @param {{ skipAnimation?: boolean, restoreFocus?: boolean } | Event} [options]
   */
  closeDialog = async (options = {}) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open || this.#isClosing) return;

    const skipAnimation = 'skipAnimation' in options && options.skipAnimation === true;
    const restoreFocus = 'restoreFocus' in options && options.restoreFocus === true;

    this.#isClosing = true;
    document.removeEventListener('click', this.#handleDocumentClick, true);

    if (!skipAnimation) {
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, { subtree: false });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';
    this.#isClosing = false;

    this.headerComponent?.releaseOverlay?.(this, { restoreFocus });
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Handles light-dismiss clicks without blocking clicks on other header controls.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open || !(event.target instanceof Element)) return;

    if (dialog.contains(event.target)) return;

    if (event.target.closest('[data-testid="cart-drawer-trigger"]')) {
      event.preventDefault();
      event.stopPropagation();
      void this.closeDialog();
      return;
    }

    if (event.target.closest('#header-component')) {
      void this.closeDialog({ skipAnimation: true });
      return;
    }

    void this.closeDialog();
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
